import ROUTES from "@constants/routing";
import { ClientContext, DEFAULT_CLIENT } from "@context/Client/ClientContext";
import { Box, Skeleton, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useClient } from "@services/api";
import { ClientModel, ClientShort } from "@services/api/types";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

const CustomClientCard = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  padding: "1rem",
  boxShadow: theme.shadows[4],
  display: "flex",
  flexDirection: "column",
  gap: "5px"
}));

type ClientCardProps = {
  clientShort: ClientShort;
  alignItems: "start" | "center" | "end";
  enabled: boolean;
};

const ClientCard = ({
  clientShort,
  alignItems = "start",
  enabled
}: ClientCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { saveCurrentClient } = useContext(ClientContext);
  const { data: client, isFetching: isLoadingClient } = useClient(
    clientShort.id.toString()
  );

  const cursor = enabled && !isLoadingClient ? "pointer" : "default";
  const onHoverEffect =
    enabled && !isLoadingClient
      ? {
          backgroundColor: theme.palette.primary.main,
          boxShadow: theme.shadows[8],
          color: theme.palette.primary.contrastText
        }
      : {};

  const handleClick = () => {
    if (enabled && !isLoadingClient) {
      // Update UserContext with the new client_id
      saveCurrentClient({
        client: client || DEFAULT_CLIENT.client,
        model: { ...DEFAULT_CLIENT.model } as ClientModel
      });
      // Navigate to the route
      navigate(ROUTES.MMM_MODEL);
    }
  };

  return (
    <Box className="group" onClick={handleClick}>
      <CustomClientCard
        sx={{
          alignItems,
          cursor,
          "&:hover": onHoverEffect
        }}
      >
        <Box
          className="w-full"
          sx={{ display: "flex", justifyContent: alignItems }}
        >
          {isLoadingClient ? (
            <Skeleton
              variant="rectangular"
              sx={{
                borderRadius: 1,
                width: { xs: "5rem", md: "8rem", lg: "14rem" },
                height: { xs: "2rem", md: "4rem", lg: "8rem" }
              }}
            />
          ) : (
            <Box
              component="img"
              src={`data:image/png;base64,${client?.logo_path}`}
              alt={`${client?.name} logo`}
              className="group-hover:fill-white object-contain m-4"
              sx={{
                height: { xs: "1rem", md: "2rem", lg: "4rem" }
              }}
            />
          )}
        </Box>
        {isLoadingClient ? (
          <Skeleton
            variant="text"
            sx={{ fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.334 }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: { xs: "0.5rem", md: "0.75rem", lg: "1rem" },
              fontWeight: { xs: 400, md: 600, lg: 800 }
            }}
          >
            {clientShort.label || clientShort.name}
          </Typography>
        )}
      </CustomClientCard>
    </Box>
  );
};

export default ClientCard;
